/**
 * Form primitives shared by the entity editors.
 *
 * ServiceEditor and SiteConfigEditor had byte-identical copies of Section,
 * Field and ToggleField; ColorField existed only in SiteConfigEditor but is
 * generic. They live here so the two editors cannot drift apart visually.
 *
 * These wrap the shared @/components/ui primitives — they are layout
 * conventions for this feature, not new UI components.
 */

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { ChevronDown, ChevronRight } from "lucide-react"

/** Collapsible titled group. */
export function Section({
  title, children, defaultOpen = false,
}: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-2 py-2 text-sm font-medium hover:bg-muted/40"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {title}
      </button>
      {open && <div className="space-y-2 border-t p-2">{children}</div>}
    </div>
  )
}

/** Labelled field with an optional hint below the control. */
export function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

/** Label + hint on the left, switch on the right. */
export function ToggleField({
  label, hint, checked, onChange,
}: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-2 py-1">
      <div>
        <Label className="text-sm">{label}</Label>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

/**
 * Colour swatch plus a free-text field.
 *
 * The text field is authoritative: Directus stores free hex/named values, so
 * the native picker (which only understands #RRGGBB) falls back to white
 * rather than clobbering a named colour it cannot represent.
 */
export function ColorField({
  label, value, onChange,
}: { label: string; value?: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground capitalize">{label}</Label>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          aria-label={`${label} colour picker`}
          value={value && /^#[0-9a-f]{6}$/i.test(value) ? value : "#ffffff"}
          onChange={e => onChange(e.target.value)}
          className="h-8 w-8 shrink-0 cursor-pointer rounded border"
        />
        <Input value={value ?? ""} onChange={e => onChange(e.target.value)} className="h-8 text-sm" placeholder="#RRGGBB" />
      </div>
    </div>
  )
}
